import { useEffect, useState } from 'react';
import { countdownTo } from '../format';

interface CountdownProps {
  /** ISO instant to count towards. */
  targetIso: string;
  /** Shown once the target has passed. */
  overLabel: string;
}

/**
 * A ticking countdown to the end (or start) of a race.
 *
 * Ticks once a second off `Date.now()` rather than by decrementing a stored
 * value: a decrementing timer drifts, and drifts badly on a phone that has been
 * asleep — waking up to a clock that says three days when the race ended
 * yesterday is worse than no clock at all.
 *
 * The whole thing is one `aria-live="off"` region with a static text
 * alternative, because a screen reader announcing a new time every second is
 * unusable. The date range beside it carries the same information calmly.
 */
export function Countdown({ targetIso, overLabel }: CountdownProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // Recreated whenever the target changes, so switching races cannot leave a
    // timer running against the old one.
    const id = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(id);
  }, [targetIso]);

  const remaining = countdownTo(targetIso, now);

  if (remaining.isOver) {
    return <p className="text-sm font-medium text-surface-400">{overLabel}</p>;
  }

  return (
    <div className="countdown flex items-center gap-1.5" aria-live="off">
      <Unit value={remaining.days} label="gün" />
      <Separator />
      <Unit value={remaining.hours} label="saat" />
      <Separator />
      <Unit value={remaining.minutes} label="dk" />
      <Separator />
      <Unit value={remaining.seconds} label="sn" />
    </div>
  );
}

function Unit({ value, label }: { value: number; label: string }) {
  return (
    <div className="countdown-unit flex min-w-11 flex-col items-center rounded-lg bg-surface-950/70 px-2 py-1.5">
      <span className="countdown-value font-mono text-lg font-semibold leading-none text-surface-50 tabular-nums">
        {String(value).padStart(2, '0')}
      </span>
      <span className="countdown-label mt-1 text-xs uppercase tracking-wider text-surface-500">
        {label}
      </span>
    </div>
  );
}

function Separator() {
  return (
    <span aria-hidden className="text-surface-700">
      :
    </span>
  );
}
